/**
 * Wires the Android hardware back button into the shell. On the native build the
 * back press should unwind whatever's open a step at a time, not quit the app.
 *
 * Three registrations, by priority, so the right thing closes first:
 *   - 30  transient overlays on top of everything (a confirm dialog, the
 *         track/boat popover, the long-press ring, the data-field picker, a
 *         tapped tidal diamond, and any armed chart tool waiting for a tap).
 *   - 20  the menu's own drill levels (registered inside MenuSheet), so the menu
 *         walks back one level per press before the sheet closes.
 *   - 10  a generic close for any other open sheet.
 * If none of them consume the press, androidBack lets the OS exit the app.
 *
 * Renders nothing; it only exists to hold the effect + registrations.
 */
import { useEffect } from 'react';
import { useShellStore } from '@/store/shell';
import { installAndroidBackButton } from './androidBack';
import { useBackHandler } from './useBackHandler';

export function AndroidBackRunner() {
  useEffect(() => {
    void installAndroidBackButton();
  }, []);

  // Priority 30: the modal-most things, closed before anything underneath.
  useBackHandler(() => {
    const s = useShellStore.getState();
    if (s.confirm) {
      s.clearConfirm();
      return true;
    }
    if (s.trackMenu) {
      s.closeTrackMenu();
      return true;
    }
    if (s.ring) {
      s.closeRing();
      return true;
    }
    if (s.editingFieldIndex != null) {
      s.setEditingFieldIndex(null);
      return true;
    }
    if (s.diamondTap) {
      s.setDiamondTap(null);
      return true;
    }
    // An armed chart tool has hidden its sheet and is waiting for a tap; back
    // cancels the arming rather than falling through to exit. The transit/heading
    // tools have clean clears; the fix pick reopens its builder without capturing.
    if (s.transitArmed || s.pendingTransit) {
      s.clearPendingTransit();
      return true;
    }
    if (s.headingArmed) {
      s.clearHeadingArm();
      return true;
    }
    if (s.fixArmed) {
      // Reopens the fix/running-fix sheet (pickReturn) and drops the armed state,
      // keeping the draft observations gathered so far.
      s.setPendingMark(null);
      return true;
    }
    if (s.anchorPickArmed) {
      // Without this, back while waiting for the anchor tap falls through to
      // "exit the app", which is a poor thing to do to someone who is anchoring.
      s.cancelAnchorPick();
      return true;
    }
    return false;
  }, 30);

  // Priority 10: close any remaining open sheet. The menu is handled by its own
  // priority-20 handler (it unwinds its drill levels first), so skip it here.
  useBackHandler(() => {
    const s = useShellStore.getState();
    if (s.activeSheet && s.activeSheet !== 'menu') {
      s.closeSheet();
      return true;
    }
    return false;
  }, 10);

  return null;
}
